/**
 * Workout Generation Engine
 * Combines templates + periodization + user data to generate actual workouts
 */

import type { 
  WorkoutProgram, 
  WorkoutDay, 
  PeriodizationPhaseConfig 
} from './types/program';
import { getPeriodizationPhase } from './periodization';
import { enrichWorkoutTemplate } from './workoutEnrichment';
import { isLoadPlusBodyweightLift } from './oneRM';
import { getTrackingType, type TrackingType } from './exerciseDatabase';

export interface WorkoutTemplate {
  name: string;
  sessionType: 'pt' | 'assessment' | 'conditioning' | 'range';
  estimatedDuration?: number;
  blocks: WorkoutBlock[];
}

export interface WorkoutBlock {
  name: string;
  type: 'prep' | 'strength' | 'accessory' | 'conditioning' | 'recovery' | 'circuit' | 'range' | 'finisher';
  format?: 'superset' | 'circuit' | 'standard' | 'amrap'; // Block-level format for grouping exercises
  exercises: TemplateExercise[];
}

/**
 * Resolve an exercise's format from the block's and its own.
 *
 * GROUPING formats (circuit / superset / giant-set / amrap / emom / flow)
 * describe how the BLOCK runs, so the block wins — a plank inside a circuit is
 * still performed as part of that circuit.
 *
 * Everything else is a TRACKING concern belonging to the exercise, and there
 * the block used to win unconditionally. That is how Taskforce Spearhead's
 * `{ name: 'Plank', sets: 3, reps: '45s', format: 'bodyweight' }` inherited its
 * strength block's format: the generator then treated it as a loaded lift and
 * assigned a first-time starting weight, `target_load === 0` failed in the
 * timed-render check, and the plank displayed as "45 REPS" instead of a
 * 45-second hold. The reps parser handled "45s" correctly all along — the
 * per-exercise format was the part being thrown away.
 */
const GROUPING_FORMATS = new Set([
  'superset', 'circuit', 'giant-set', 'giant set', 'amrap', 'emom',
  'flow', 'contrast', 'straight-sets',
]);

export function resolveExerciseFormat(
  blockFormat: string | undefined,
  exerciseFormat: string | undefined,
  blockType: string | undefined,
): string {
  const bf = (blockFormat || '').toLowerCase();
  if (bf && GROUPING_FORMATS.has(bf)) return blockFormat!;
  return exerciseFormat || blockFormat || blockType || 'standard';
}

export interface TemplateExercise {
  name: string;
  sets?: number | string;  // e.g., 5 or "3-4" or "AMRAP"
  reps?: number | string;  // e.g., 5 or "8-10" or "Max"
  intensity?: 'progressive' | 'fixed' | string;  // "progressive" uses periodization, "fixed" uses RPE/%, or direct value like "75%"
  rpe?: string;  // RPE target (e.g., "RPE 7-8")
  rest?: string;
  details?: string;
  format?: string;  // "standard", "superset", "circuit", "AMRAP", etc.
  duration?: string;  // for timed exercises
  distance?: string;  // for distance-based exercises
  operatorFocus?: string;
  percentRM?: number;  // For 1RM-based loading (e.g., 55 = 55% of 1RM)
}

// Adjustment source tracking for ML transparency
export interface AdjustmentSource {
  type: 'periodization' | 'recovery' | 'ml' | 'statistics' | 'deload' | 'acwr' | 'rule';
  multiplier: number;
  reason: string;
}

export interface AppliedAdjustment {
  applied: boolean;
  baselineLoad: number;           // What the program template specified
  baselineReps?: number;          // Original reps if adjusted
  baselineDistance?: number;      // Original distance if adjusted (meters)
  baselineIncline?: number;       // Original incline if adjusted
  baselineSetsReps?: string;      // Original sets/reps/distance/duration string (for cardio revert)
  sources: AdjustmentSource[];    // Chain of adjustments applied
  totalMultiplier: number;        // Combined multiplier (e.g., 0.85)
  explanation: string;            // Human-readable summary
}

export interface GeneratedExercise {
  name: string;
  block: string;
  format: string;
  sets_reps: string;
  intensity: string;
  target_load: number;            // FINAL value after ML adjustments
  targetDistance?: number;        // For ruck/cardio exercises (in meters)
  targetIncline?: number;         // For treadmill exercises (0-15%)
  rest: string;
  details: string;
  rpe: string;
  sessionType?: string;

  // How this exercise is tracked/logged, from the curated exercise DB
  // (lib/exerciseDatabase.ts). Optional because GeneratedExercise is also
  // constructed outside this generator (lib/templateToWorkout.ts, injury
  // prehab/posthab in smartWorkoutGenerator, synthetic entries in
  // WorkoutDisplayV2) — consumers must treat `undefined` as "unknown, fall
  // back to name-based classification", NOT as weight-reps. Both generator
  // paths in this file always populate it (DB lookup with an explicit
  // prescription-derived fallback — see resolveTrackingType).
  trackingType?: TrackingType;

  // ML Adjustment tracking - shows what changed and why
  adjustment?: AppliedAdjustment;
}

/**
 * Resolve the tracking type for a template exercise.
 *
 * Source of truth is the curated exercise DB (getTrackingType → findExercise).
 * On a DB miss we infer ONLY from a positive signal the program author gave:
 *   - a duration prescription → 'time'
 *   - a distance prescription → 'distance-time'
 *   - intensity authored as bodyweight (any casing) → 'bodyweight-reps'
 *
 * With no signal we return `undefined` rather than guessing 'weight-reps'.
 * That is deliberate: a fabricated stamp is indistinguishable from DB truth to
 * consumers (WorkoutDisplayV2's dbRenderKind reads `ex.trackingType ?? lookup`),
 * so guessing here silently disables the name-based fallback that classifies
 * DB-miss names like "2K Row or 10-Min Bike" (cardio) or "Copenhagen Plank"
 * (bodyweight). Leaving it undefined is what the GeneratedExercise contract
 * above tells consumers to expect and handle.
 *
 * `resolvedIntensity` is the intensity string the generator computed for the
 * exercise (it becomes 'Bodyweight' when the author specified none), so the
 * bodyweight inference covers both authored and generator-defaulted cases.
 */
function resolveTrackingType(
  exercise: TemplateExercise,
  resolvedIntensity: string
): TrackingType | undefined {
  const fromDb = getTrackingType(exercise.name);
  if (fromDb) return fromDb;

  if (exercise.duration) return 'time';
  if (exercise.distance) return 'distance-time';
  const intensity = (resolvedIntensity || String(exercise.intensity || '')).toLowerCase();
  if (intensity === 'bodyweight') return 'bodyweight-reps';
  return undefined;
}

/**
 * Get the periodization phase for a specific week
 */
function getPhaseForWeek(
  program: WorkoutProgram,
  week: number
): PeriodizationPhaseConfig | null {
  if (!program.periodization?.phases || program.periodization.phases.length === 0) {
    return null;
  }

  for (const phase of program.periodization.phases) {
    if (phase.weeks.includes(week)) {
      return phase;
    }
  }

  return null;
}

/**
 * Calculate intensity for a given week within a phase
 */
function calculatePhaseIntensity(
  phase: PeriodizationPhaseConfig,
  weekInPhase: number
): number {
  // If phase has no progression, use base multiplier
  if (!phase.progression) {
    return phase.intensityMultiplier;
  }

  const { startIntensity, weeklyIncrease } = phase.progression;
  
  // Week 1 of phase = index 0
  const weekIndex = weekInPhase - 1;
  
  return startIntensity + (weekIndex * weeklyIncrease);
}

/**
 * Exercise name to 1RM key mapping for known exercises
 * This prevents dangerous fuzzy matches (e.g., "Incline Dumbbell Press" matching "Bench Press")
 */
const EXERCISE_TO_1RM_KEY: Record<string, string> = {
  // Core barbell lifts
  'Deadlift': 'deadlift',
  'Back Squat': 'backSquat',
  'Barbell Squat': 'backSquat',
  'Front Squat': 'frontSquat',
  'Bench Press': 'benchPress',
  'Barbell Bench Press': 'benchPress',
  'Overhead Press': 'overheadPress',
  'Military Press': 'overheadPress',
  'Barbell Row': 'row',
  'Pendlay Row': 'row',
  'Barbell Row (Pendlay)': 'row',
  
  // Dumbbell variations - these should NOT use barbell 1RMs
  'Incline Dumbbell Press': 'inclineDumbbellPress',
  'Incline Dumbbell Bench Press': 'inclineDumbbellPress',
  'Incline DB Press': 'inclineDumbbellPress',
  'Dumbbell Bench Press': 'dumbbellBenchPress',
  'Dumbbell Press': 'dumbbellBenchPress',
  'Dumbbell Shoulder Press': 'dumbbellShoulderPress',
  'Seated Dumbbell Press': 'dumbbellShoulderPress',
  'Arnold Press': 'arnoldPress',
  'Dumbbell Row': 'dumbbellRow',
  
  // Other compound movements
  'Pull-Ups': 'pullUps',
  'Weighted Pull-Up': 'weightedPullUp',
  'Weighted Pull-Ups': 'weightedPullUp',
  'Close-Grip Bench Press': 'closeGripBench',
  'Incline Bench Press': 'inclineBenchPress',
  'Decline Bench Press': 'declineBenchPress',
  'Sumo Deadlift': 'sumoDeadlift',
  'Romanian Deadlift': 'romanianDeadlift',
  'Trap Bar Deadlift': 'trapBarDeadlift',
  'Power Clean': 'powerClean',
  'Push Press': 'pushPress',
  
  // Accessories with specific 1RMs
  'Barbell Curl': 'barbellCurl',
  'Skull Crusher': 'skullCrusher',
  'Tricep Pushdown': 'tricepPushdown',
};

/**
 * Calculate target load for an exercise
 * Uses strict matching to avoid dangerous cross-exercise 1RM matching
 */
function calculateTargetLoad(
  exerciseName: string,
  intensity: number,
  reps: number,
  oneRMs: Record<string, number>
): number {
  // WEIGHTED PULL-UP / DIP GUARD (audit P0-7 — safety fix, 2026-07-14).
  // %1RM math on a bodyweight-plus-load lift is computed against the ADDED
  // load only, ignoring bodyweight — which OVER-prescribes (at BW 200 /
  // added-1RM 100, "84%" = 84 lb added = 94.7% of the true system max). The
  // stored value may also be a rep count, not a load. See
  // isLoadPlusBodyweightLift in oneRM.ts for the full rationale. Returning 0
  // routes these lifts to the last-used-weight / statistical-history path,
  // which compares added load to added load and cannot ratio-inflate.
  if (isLoadPlusBodyweightLift(exerciseName)) {
    console.log(` %1RM derivation skipped for "${exerciseName}" — bodyweight+load lift (added-load % math over-prescribes)`);
    return 0;
  }

  // Simple key lookup - try exact match first, then case-insensitive
  const normalizedName = exerciseName.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  // Try to find a matching 1RM key
  let exerciseKey: string | undefined;
  let oneRM = 0;
  
  // 0. FIRST: Check explicit exercise mapping (prevents dangerous fuzzy matches)
  if (EXERCISE_TO_1RM_KEY[exerciseName]) {
    const mappedKey = EXERCISE_TO_1RM_KEY[exerciseName];
    if (oneRMs[mappedKey] && oneRMs[mappedKey] > 0) {
      exerciseKey = mappedKey;
      oneRM = oneRMs[mappedKey];
      console.log(` [MAPPED] "${exerciseName}" → key "${mappedKey}" = ${oneRM} lbs`);
    }
  }
  
  // 1. Try exact key match (camelCase)
  if (!oneRM) {
    const camelKey = exerciseName
      .split(/\s+/)
      .map((word, i) => {
        const clean = word.toLowerCase().replace(/[^a-z0-9]/g, '');
        return i === 0 ? clean : clean.charAt(0).toUpperCase() + clean.slice(1);
      })
      .join('');
    
    if (oneRMs[camelKey] && oneRMs[camelKey] > 0) {
      exerciseKey = camelKey;
      oneRM = oneRMs[camelKey];
    }
  }
  
  // 2. Try lowercase key (no spaces)
  if (!oneRM) {
    const lowerKey = normalizedName;
    if (oneRMs[lowerKey] && oneRMs[lowerKey] > 0) {
      exerciseKey = lowerKey;
      oneRM = oneRMs[lowerKey];
    }
  }
  
  // NO FUZZY MATCHING - too dangerous for 1RMs
  // If we don't have an exact match, return 0 and let the user set it manually
  if (!oneRM) {
    console.log(` No 1RM found for "${exerciseName}" - add it to EXERCISE_TO_1RM_KEY mapping or set manually`);
    return 0;
  }

  // For higher rep ranges, adjust intensity down
  // NOTE: HST and other programs with explicit %1RM already account for rep ranges
  // Only apply rep adjustment for periodized programs using progressive intensity
  // This prevents double-adjustment that causes weights to be too low
  let repAdjustment = 1.0;
  // DISABLED: Programs like HST specify exact %1RM per rep range
  // if (reps > 5) {
  //   repAdjustment = 1.0 - ((reps - 5) * 0.025);
  // }

  const targetLoad = Math.round(oneRM * intensity * repAdjustment);
  console.log(` Target load for "${exerciseName}" (key: ${exerciseKey}): ${oneRM} × ${(intensity * 100).toFixed(0)}% × repAdj=${repAdjustment.toFixed(2)} = ${targetLoad} lbs`);
  
  return targetLoad;
}

/**
 * Parse sets and reps from template
 */
function parseSetsReps(
  sets?: number | string,
  reps?: number | string
): { setsStr: string; repsNum: number } {
  const setsStr = sets?.toString() || '3';
  const repsStr = reps?.toString() || '10';
  
  // Extract first number for calculations
  const repsNum = parseInt(repsStr.match(/\d+/)?.[0] || '10');
  
  return { setsStr, repsNum };
}

/**
 * Apply periodization adjustments to sets and reps
 * This is where the MAGIC happens - transforms base template based on training phase
 * Exported for unit testing (see periodizationRepScaling.test.ts).
 */
export function applyPeriodizationAdjustments(
  sets: number | string,
  reps: number | string,
  phase: PeriodizationPhaseConfig,
  isStrengthExercise: boolean
): { adjustedSets: string; adjustedReps: string } {
  // Parse original values
  const baseSets = typeof sets === 'number' ? sets : parseInt(sets.toString().match(/\d+/)?.[0] || '3');
  const baseRepsStr = typeof reps === 'number' ? reps.toString() : reps.toString();
  
  // Don't adjust non-numeric reps (like "AMRAP", "Max", "each", etc.)
  if (!/^\d+$/.test(baseRepsStr)) {
    return {
      adjustedSets: sets.toString(),
      adjustedReps: baseRepsStr
    };
  }

  let adjustedSets = baseSets;
  let adjustedReps = parseInt(baseRepsStr);

  // Apply training parameters if available AND it's a strength exercise
  if (phase.trainingParameters && isStrengthExercise) {
    const { repRange, setsMultiplier } = phase.trainingParameters;

    // Adjust sets based on phase multiplier
    adjustedSets = Math.round(baseSets * setsMultiplier);

    // Respect the template's authored rep scheme. The block rep-range only
    // TRIMS reps down toward its ceiling in lower-rep (peaking) blocks — it
    // never inflates an intentionally heavy low-rep strength signal up into
    // hypertrophy reps.
    //
    // The previous logic assumed every strength lift was 5-rep based and
    // snapped to the range midpoint: reps × (midpoint / 5), clamped to the
    // range. Because range reps like '8-12' are non-numeric and exempt
    // (guarded above), the ONLY lifts this touched were the heavy integer-rep
    // signals — so e.g. a 5x3 weighted pull-up silently became 5x8 in an
    // 8-12 accumulation block, inverting the strength intent. We now keep the
    // authored reps and only cap them at the block ceiling.
    const maxReps = repRange[1];
    const baseRepsNum = parseInt(baseRepsStr);
    adjustedReps = baseRepsNum > maxReps ? maxReps : baseRepsNum;
  } else {
    // For non-strength exercises (accessories, etc.), use volume multiplier
    adjustedSets = Math.round(baseSets * phase.volumeMultiplier);
  }

  return {
    adjustedSets: adjustedSets.toString(),
    adjustedReps: adjustedReps.toString()
  };
}

/**
 * Generate a workout from a template for programs that use an explicit
 * periodization model (Operator / Recruit / Grunt / Specialist).
 */
export function generateWorkout(
  template: WorkoutTemplate,
  program: WorkoutProgram,
  week: number,
  oneRMs: Record<string, number>,
  recoveryAdjustments?: {
    volumeMultiplier: number;
    intensityMultiplier: number;
  }
): GeneratedExercise[] {
  // Enrich the template with comprehensive prep/recovery blocks
  // Programs with detailed templates (usesPeriodization + substantial blocks)
  // define their own day-specific prep/recovery — don't overwrite them.
  // Only force-replace for programs with minimal/generic blocks.
  const hasDetailedBlocks = program.metadata?.usesPeriodization &&
    template.blocks?.some((b: any) => b.type === 'prep' && b.exercises?.length >= 4);
  const enrichedTemplate = enrichWorkoutTemplate(template, {
    replacePrepIfEmpty: true,
    replaceRecoveryIfEmpty: true,
    forceReplace: !hasDetailedBlocks,
  });
  
  // Get the periodization phase for this week
  let phase = getPhaseForWeek(program, week);

  // For programs without periodization, use a default neutral phase
  if (!phase) {
    console.log(` No periodization defined, using default neutral phase for week ${week}`);
    phase = {
      name: 'Training Week',
      type: 'maintenance',
      weeks: [week],
      focus: 'Follow template as written',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 1.0,
        restPeriod: '60-90s',
      },
    };
  }
  
  // Calculate which week we are within this phase
  const weekInPhase = week - phase.weeks[0] + 1;
  
  // Calculate the intensity for this specific week
  const phaseIntensity = calculatePhaseIntensity(phase, weekInPhase);
  
  console.log(` Generating workout for Week ${week}:`, {
    phase: phase.name,
    weekInPhase,
    baseIntensity: phaseIntensity,
    volumeMultiplier: phase.volumeMultiplier,
    intensityMultiplier: phase.intensityMultiplier,
    trainingParameters: phase.trainingParameters
  });

  const exercises: GeneratedExercise[] = [];

  for (const block of enrichedTemplate.blocks) {
    // Determine if this is a strength block
    const isStrengthBlock = block.type === 'strength';
    
    for (const exercise of block.exercises) {
      const { setsStr, repsNum } = parseSetsReps(exercise.sets, exercise.reps);

      // Apply periodization adjustments to sets and reps for strength exercises
      const { adjustedSets, adjustedReps } = applyPeriodizationAdjustments(
        setsStr,
        exercise.reps || '10',
        phase,
        isStrengthBlock
      );

      let intensity = ''; // Display string
      let targetLoad = 0;
      let restPeriod = exercise.rest || '60-90s';

      // Use phase-specific rest periods for strength exercises
      if (isStrengthBlock && phase.trainingParameters?.restPeriod) {
        restPeriod = phase.trainingParameters.restPeriod;
      }

      // Determine intensity and target load
      if (exercise.intensity === 'progressive') {
        // Use periodization intensity
        const percentIntensity = Math.round(phaseIntensity * 100);
        intensity = `${percentIntensity}%`;
        
        // Use adjusted reps for load calculation
        const adjustedRepsNum = parseInt(adjustedReps.match(/\d+/)?.[0] || repsNum.toString());
        targetLoad = calculateTargetLoad(
          exercise.name,
          phaseIntensity,
          adjustedRepsNum,
          oneRMs
        );

        // Apply recovery adjustments if provided
        if (recoveryAdjustments) {
          targetLoad = Math.round(targetLoad * recoveryAdjustments.intensityMultiplier);
        }
      } else if (exercise.intensity === 'fixed') {
        // Use RPE or fixed percentage
        intensity = exercise.rpe || 'RPE 7-8';
        targetLoad = 0; // User determines
      } else if (exercise.intensity) {
        // Direct value like "75%" or "RPE 8"
        intensity = exercise.intensity;

        // If it's a percentage, calculate load
        const percentMatch = exercise.intensity.match(/(\d+)%/);
        if (percentMatch) {
          const percent = parseInt(percentMatch[1]) / 100;
          const adjustedRepsNum = parseInt(adjustedReps.match(/\d+/)?.[0] || repsNum.toString());
          targetLoad = calculateTargetLoad(
            exercise.name,
            percent,
            adjustedRepsNum,
            oneRMs
          );

          // Apply recovery adjustments if provided
          if (recoveryAdjustments) {
            targetLoad = Math.round(targetLoad * recoveryAdjustments.intensityMultiplier);
          }
        }
      } else if (exercise.percentRM && exercise.percentRM > 0) {
        // Use percentRM field for 1RM-based loading
        const percent = exercise.percentRM / 100;
        intensity = `${exercise.percentRM}% 1RM`;
        const adjustedRepsNum = parseInt(adjustedReps.match(/\d+/)?.[0] || repsNum.toString());
        targetLoad = calculateTargetLoad(
          exercise.name,
          percent,
          adjustedRepsNum,
          oneRMs
        );

        // Apply recovery adjustments if provided
        if (recoveryAdjustments) {
          targetLoad = Math.round(targetLoad * recoveryAdjustments.intensityMultiplier);
        }
      } else {
        /**
         * No intensity authored — do not CLAIM bodyweight.
         *
         * This labelled every un-authored exercise 'Bodyweight', including
         * loaded movements. Seen in the QA clickthrough: a circuit's
         * Kettlebell Swing and Goblet Squat both read "Bodyweight" with an
         * empty weight cell, despite the audited DB declaring them
         * `weight-reps`. The athlete is told a loaded lift is bodyweight.
         *
         * The DB is the authority on whether a movement carries load. When it
         * says the exercise is loaded, say nothing rather than something
         * false — the weight cell stays editable and the ML fills it from
         * history. `targetLoad` stays 0 either way; that part was never the
         * lie.
         */
        const tt = getTrackingType(exercise.name);
        const isTrulyUnloaded =
          tt === 'bodyweight-reps' || tt === 'time' ||
          (tt === undefined && String(exercise.intensity || '').toLowerCase() === 'bodyweight');
        intensity = isTrulyUnloaded ? 'Bodyweight' : '';
        targetLoad = 0;
      }

      // Apply recovery volume adjustment to sets
      let finalSets = adjustedSets;
      if (recoveryAdjustments && recoveryAdjustments.volumeMultiplier < 1) {
        const numSets = parseInt(String(adjustedSets)) || 3;
        const reducedSets = Math.max(1, Math.round(numSets * recoveryAdjustments.volumeMultiplier));
        finalSets = String(reducedSets);
      }

      // Build sets × reps string with adjusted values
      let setsRepsStr = '';
      if (exercise.duration) {
        // Only use "sets × duration" format if exercise explicitly defined sets (e.g., intervals).
        // Duration-only exercises (zone 2 cardio, stretches) should display just the duration.
        const explicitSets = exercise.sets;
        const sets = explicitSets ? (typeof explicitSets === 'number' ? explicitSets : parseInt(String(explicitSets)) || 1) : 1;
        const finalDurationSets = recoveryAdjustments && recoveryAdjustments.volumeMultiplier < 1
          ? Math.max(1, Math.round(sets * recoveryAdjustments.volumeMultiplier))
          : sets;
        if (finalDurationSets > 1) {
          // Interval format: "5 × 0:30 / 1:00 rest"
          setsRepsStr = exercise.rest
            ? `${finalDurationSets} × ${exercise.duration} / ${exercise.rest} rest`
            : `${finalDurationSets} × ${exercise.duration}`;
        } else {
          setsRepsStr = exercise.duration;
        }
      } else if (exercise.distance) {
        setsRepsStr = exercise.distance;
      } else if (exercise.reps) {
        setsRepsStr = `${finalSets} × ${adjustedReps}`;
      } else {
        setsRepsStr = finalSets;
      }

      exercises.push({
        name: exercise.name,
        block: block.name,
        format: resolveExerciseFormat(block.format, exercise.format, block.type),
        sets_reps: setsRepsStr,
        intensity,
        target_load: targetLoad,
        rest: restPeriod,
        details: exercise.details || '',
        /**
         * ALWAYS carry the authored effort target.
         *
         * This was `exercise.intensity === 'fixed' ? (exercise.rpe || '') : ''`
         * — and NO shipped program uses `intensity: 'fixed'`. Measured across
         * all 2,897 authored exercises: 0 are 'fixed', so the true branch was
         * dead and this expression evaluated to '' every single time. 53
         * exercises across the shipped programs author an `rpe` and every one
         * of them had it discarded here.
         *
         * The damage is not only cosmetic. `intensity` is separately
         * overwritten further up with the periodization percentage
         * ("progressive" -> "100%"), so by the time WorkoutDisplayV2 computes
         * `rirTarget: parseRirTarget(ex.intensity, ex.rpe)` BOTH inputs are
         * gone and rirTarget is null. That field is what the save path encodes
         * into `targetRPE`, and what lib/patternMemory.ts reads for its
         * session-quality score.
         *
         * Found by clicking through the QA program: Back Squat authored
         * `rpe: 'RIR 2'` arrived in the browser as
         * `intensity: "100%", rpeRange: "", rirTarget: null`, so neither the
         * list nor the focus layout could display a target — which is what
         * made the two layouts look inconsistent in the first place.
         *
         * A bare number means RPE; a string is already labelled ("RIR 2").
         * Same normalisation the sibling branch at ~:723 already applies.
         */
        rpe: exercise.rpe === undefined || exercise.rpe === null || exercise.rpe === ''
          ? ''
          : typeof exercise.rpe === 'number'
            ? `RPE ${exercise.rpe}`
            : String(exercise.rpe),
        sessionType: template.sessionType,
        // Spread, not `trackingType: resolve(...)` — on a DB miss with no
        // prescription signal the key must be ABSENT, not present-and-undefined
        // (these objects reach Firestore, which rejects undefined values).
        ...(() => {
          const tt = resolveTrackingType(exercise, intensity);
          return tt ? { trackingType: tt } : {};
        })()
      });
    }
  }

  return exercises;
}

/**
 * Parse week-specific percentage from details field
 * Supports formats like:
 * - "Week 2: 55% 1RM, Week 3: 60% 1RM"
 * - "Week 4: 60% 1RM, Week 5: 65-70% 1RM"
 * Returns the percentage as a decimal (0.55) or null if not found
 */
function parseWeekSpecificPercentage(details: string, currentWeek: number): number | null {
  if (!details) return null;
  
  // Pattern: "Week X: YY% 1RM" or "Week X: YY-ZZ% 1RM" (use the lower value for safety)
  const weekPattern = /Week\s*(\d+)\s*:\s*(\d+(?:\.\d+)?)\s*(?:-\s*\d+(?:\.\d+)?)?\s*%\s*1RM/gi;
  let match: RegExpExecArray | null;
  
  while ((match = weekPattern.exec(details)) !== null) {
    const weekNum = parseInt(match[1], 10);
    const percent = parseFloat(match[2]);
    
    if (weekNum === currentWeek && !isNaN(percent)) {
      return percent / 100;
    }
  }
  
  // Also try: "Weeks X-Y: ZZ% 1RM" format (use single percentage for both weeks)
  const rangePattern = /Weeks?\s*(\d+)\s*-\s*(\d+)\s*:\s*(\d+(?:\.\d+)?)\s*%\s*1RM/gi;
  
  while ((match = rangePattern.exec(details)) !== null) {
    const startWeek = parseInt(match[1], 10);
    const endWeek = parseInt(match[2], 10);
    const percent = parseFloat(match[3]);
    
    if (currentWeek >= startWeek && currentWeek <= endWeek && !isNaN(percent)) {
      return percent / 100;
    }
  }
  
  // Fallback: Generic percentage range like "55-60% 1RM" - use appropriate one based on week parity
  const rangeMatch = details.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*%\s*1RM/i);
  if (rangeMatch) {
    const lowPercent = parseFloat(rangeMatch[1]);
    const highPercent = parseFloat(rangeMatch[2]);
    // For HST: even weeks use lower, odd weeks use higher (within 2-week blocks)
    // This is a simplification - the first week of a block uses lower, second uses higher
    const useHigher = currentWeek % 2 === 1; // Odd weeks (3, 5, 7...) in HST use higher %
    const percent = useHigher ? highPercent : lowPercent;
    return percent / 100;
  }
  
  return null;
}

/**
 * Generate a workout from a template for programs that do NOT use the
 * global periodization engine (e.g. HST, fixed-block programs).
 */
function generateNonPeriodizedWorkout(
  template: WorkoutTemplate,
  week: number,
  oneRMs: Record<string, number>,
  recoveryAdjustments?: {
    volumeMultiplier: number;
    intensityMultiplier: number;
  }
): GeneratedExercise[] {
  const exercises: GeneratedExercise[] = [];

  for (const block of template.blocks) {
    for (const exercise of block.exercises) {
      const setsVal = exercise.sets ?? 3;
      const repsVal = exercise.reps ?? 10;
      const setsStr = setsVal.toString();
      const repsStr = repsVal.toString();

      // Build sets × reps / duration string
      let setsRepsStr = '';
      if (exercise.duration) {
        // Only use "sets × duration" format if exercise explicitly defined sets (e.g., intervals).
        // Duration-only exercises (zone 2 cardio, stretches) should display just the duration.
        const explicitSets = exercise.sets;
        const sets = explicitSets ? (typeof explicitSets === 'number' ? explicitSets : parseInt(String(explicitSets)) || 1) : 1;
        if (sets > 1) {
          // Interval format: "5 × 0:30 / 1:00 rest"
          setsRepsStr = exercise.rest
            ? `${sets} × ${exercise.duration} / ${exercise.rest} rest`
            : `${sets} × ${exercise.duration}`;
        } else {
          setsRepsStr = exercise.duration;
        }
      } else if (exercise.distance) {
        setsRepsStr = exercise.distance;
      } else if (exercise.reps) {
        setsRepsStr = `${setsStr} × ${repsStr}`;
      } else {
        setsRepsStr = setsStr;
      }

      // Intensity display string
      let intensity = '';
      if (typeof exercise.intensity === 'string') {
        intensity = exercise.intensity;
      } else if (exercise.rpe !== undefined) {
        intensity = typeof exercise.rpe === 'number' ? `RPE ${exercise.rpe}` : String(exercise.rpe);
      }

      // Target load – compute from % of 1RM
      let targetLoad = 0;
      let percentUsed: number | null = null;
      
      // Priority 1: Use explicit percentRM field if available
      if (exercise.percentRM && exercise.percentRM > 0) {
        percentUsed = exercise.percentRM / 100; // Convert 55 to 0.55
      }
      
      // Priority 2: Check if intensity has a direct percentage (e.g., "75%")
      if (!percentUsed) {
        const intensityPercentMatch = intensity.match(/(\d+(?:\.\d+)?)\s*%/);
        if (intensityPercentMatch) {
          percentUsed = parseFloat(intensityPercentMatch[1]) / 100;
        }
      }
      
      // Priority 3: Parse week-specific percentage from details (legacy support)
      // Programs can specify "Week X: YY% 1RM" in details for week-specific loading
      if (!percentUsed && exercise.details) {
        percentUsed = parseWeekSpecificPercentage(exercise.details, week);
        if (percentUsed) {
          console.log(` Found ${(percentUsed * 100).toFixed(0)}% from details for "${exercise.name}" in week ${week}`);
        }
      }
      
      // If we determined a percentage (not from intensity), update the intensity display
      if (percentUsed && !intensity.includes('%')) {
        const percentDisplay = Math.round(percentUsed * 100);
        intensity = `${percentDisplay}% 1RM` + (intensity ? `, ${intensity}` : '');
      }
      
      // Calculate target load if we have a percentage
      if (percentUsed) {
        const repsNum = parseInt(repsStr.match(/\d+/)?.[0] || '8', 10);
        console.log(` Calculating target for "${exercise.name}": ${(percentUsed * 100).toFixed(0)}% × ${repsNum} reps`);
        targetLoad = calculateTargetLoad(exercise.name, percentUsed, repsNum, oneRMs);

        if (recoveryAdjustments) {
          const oldLoad = targetLoad;
          targetLoad = Math.round(targetLoad * recoveryAdjustments.intensityMultiplier);
          console.log(` Recovery adjusted: ${oldLoad} → ${targetLoad} lbs`);
        }
      } else {
        console.log(` No percentage found for "${exercise.name}" (details: "${exercise.details?.substring(0, 50)}...")`);
      }

      exercises.push({
        name: exercise.name,
        block: block.name,
        format: resolveExerciseFormat(block.format, exercise.format, block.type),
        sets_reps: setsRepsStr,
        intensity,
        target_load: targetLoad,
        rest: exercise.rest || '60s',
        details: exercise.details || '',
        rpe: typeof exercise.rpe === 'string' ? exercise.rpe : '',
        sessionType: template.sessionType,
        // See the note at the other push site: absent, never undefined.
        ...(() => {
          const tt = resolveTrackingType(exercise, intensity);
          return tt ? { trackingType: tt } : {};
        })(),
      });
    }
  }

  return exercises;
}

//  REMOVED: Runtime enrichment functions — programs define their own blocks
//  inline, keeping them self-contained and predictable.

/**
 * Get workout template for a specific week and day
 */
export function getWorkoutTemplate(
  program: WorkoutProgram,
  week: number,
  day: number
): WorkoutTemplate | null {
  console.log(` getWorkoutTemplate called: Program="${program.metadata.name}", Week=${week}, Day=${day}`);
  console.log(` Template keys available:`, Object.keys(program.templates || {}));
  // Handle custom programs that use workouts array instead of templates
  if (program.workouts && !program.templates) {
    const workout = program.workouts.find(w => w.week === week && w.day === day);
    if (!workout) {
      console.error(` No workout found for Week ${week}, Day ${day} in workouts array`);
      return null;
    }
    console.log(` Found workout in workouts array: "${workout.title}", ${workout.blocks.length} blocks`);

    // Convert WorkoutDay to WorkoutTemplate format
    const template: WorkoutTemplate = {
      name: workout.title,
      sessionType: workout.sessionType === 'assessment' ? 'assessment' : 'pt',
      estimatedDuration: workout.estimatedDuration || 60,
      blocks: workout.blocks.map(block => ({
        name: block.name,
        type: block.type as any, // Type compatibility handled at runtime
        format: (block as any).format || block.type, // Preserve format from Firestore, fallback to type
        exercises: block.exercises.map(ex => ({
          name: ex.exerciseName,
          sets: ex.sets,
          reps: ex.reps,
          duration: ex.time,
          rest: ex.rest,
          rpe: ex.rpe,
          percentRM: ex.percentRM,
          distance: ex.distance?.toString(),
          details: ex.notes,
          intensity: undefined,
          format: ex.format,
          sessionType: workout.sessionType === 'assessment' ? 'assessment' : 'pt',
        })),
      })) as any, // Type compatibility handled at runtime
    };
    
    return template;
  }

  if (!program.templates) {
    console.error(' Program has no templates defined');
    return null;
  }

  const usesPeriodization =
    program.metadata.usesPeriodization !== false &&
    !!program.periodization?.phases?.length;

  console.log(` usesPeriodization=${usesPeriodization}, metadata.usesPeriodization=${program.metadata.usesPeriodization}, phases=${program.periodization?.phases?.length || 0}`);

  //  Non-periodized programs (e.g. HST) use explicit week-based keys
  if (!usesPeriodization) {
    const directKey = `week${week}-day${day}`;
    console.log(` Looking for non-periodized template key: "${directKey}"`);
    if (program.templates[directKey]) {
      const template = program.templates[directKey] as WorkoutTemplate;
      console.log(` Found template: "${template.name}", ${template.blocks.length} blocks`);
      template.blocks.forEach((block, idx) => {
        console.log(`   Block ${idx + 1}: "${block.name}" (type: ${block.type}, format: ${block.format || 'standard'}, exercises: ${block.exercises.length})`);
      });
      return template;
    }

    // Support range keys like "week2-3-day1"
    const templateKeys = Object.keys(program.templates);
    for (const key of templateKeys) {
      const match = key.match(/^week(\d+)-(\d+)-day(\d+)$/);
      if (match) {
        const startWeek = parseInt(match[1], 10);
        const endWeek = parseInt(match[2], 10);
        const dayKey = parseInt(match[3], 10);
        if (dayKey === day && week >= startWeek && week <= endWeek) {
          const template = program.templates[key] as WorkoutTemplate;
          return template;
        }
      }
    }

    console.error(` No non-periodized template found for Week ${week}, Day ${day}`);
    console.error(' Available templates:', templateKeys);
    return null;
  }

  //  Periodized programs: check week-specific templates FIRST, then generic templates
  const currentPhase = getPhaseForWeek(program, week);
  const isTestingWeek =
    currentPhase?.type === 'assessment' || currentPhase?.type === 'realization';

  console.log(` Phase: ${currentPhase?.name || 'none'}, isTestingWeek=${isTestingWeek}`);

  const templateKeys = Object.keys(program.templates || {});

  // PRIORITY 1: Check for exact week match first (e.g., week1-day1)
  // This supports programs with different workouts per week/phase
  const exactWeekKey = `week${week}-day${day}`;
  if (program.templates[exactWeekKey]) {
    console.log(` Found exact week template: ${exactWeekKey}`);
    return program.templates[exactWeekKey] as WorkoutTemplate;
  }

  // PRIORITY 2: Check for week range match (e.g., week2-4-day1)
  for (const key of templateKeys) {
    const match = key.match(/^week(\d+)-(\d+)-day(\d+)$/);
    if (match) {
      const startWeek = parseInt(match[1], 10);
      const endWeek = parseInt(match[2], 10);
      const dayKey = parseInt(match[3], 10);
      if (dayKey === day && week >= startWeek && week <= endWeek) {
        console.log(` Found week-range template: ${key} for week ${week}`);
        return program.templates[key] as WorkoutTemplate;
      }
    }
  }

  // PRIORITY 3: For testing weeks, check testing-week-day templates
  if (isTestingWeek) {
    const testingKey = `testing-week-day${day}`;
    if (program.templates[testingKey]) {
      console.log(` Found testing template: ${testingKey}`);
      return program.templates[testingKey] as WorkoutTemplate;
    }
    console.log(` No testing template ${testingKey}, trying training template`);
  }

  // PRIORITY 4: Generic training-day templates (for programs that use same workout each week)
  const trainingKey = `training-day${day}`;
  if (program.templates[trainingKey]) {
    console.log(` Found training template: ${trainingKey}`);
    return program.templates[trainingKey] as WorkoutTemplate;
  }

  console.error(` No template found for Week ${week}, Day ${day}`);
  console.error(' Available templates:', templateKeys);
  return null;
}

/**
 * Generate a full workout for a user
 */
export function generateUserWorkout(
  program: WorkoutProgram,
  week: number,
  day: number,
  oneRMs: Record<string, number>,
  recoveryAdjustments?: {
    volumeMultiplier: number;
    intensityMultiplier: number;
  }
): GeneratedExercise[] | null {
  const template = getWorkoutTemplate(program, week, day);
  
  if (!template) {
    console.error(` No template found for Week ${week}, Day ${day}`);
    return null;
  }

  // If the program has an explicit periodization model, use the block engine;
  // otherwise, treat the template as self-contained (HST-style programs).
  const usesPeriodization =
    program.metadata.usesPeriodization !== false &&
    !!program.periodization?.phases?.length;

  if (usesPeriodization) {
    return generateWorkout(template, program, week, oneRMs, recoveryAdjustments);
  }

  return generateNonPeriodizedWorkout(template, week, oneRMs, recoveryAdjustments);
}

